<template>
  <div id="survey-questions-edit" class="pa-4">
    <div class="banner-header">
      <div>
        <v-btn
          text
          color="white"
          @click="$router.push('/survey/list')"
          class="mb-2 pl-0"
        >
          <v-icon left>mdi-arrow-left</v-icon> Back to Surveys
        </v-btn>
        <h1 class="page-title">{{ SurveyTitle }}</h1>
        <p class="mt-1 mb-0 banner-subtitle">
          <v-chip :color="statusColor(SurveyStatus)" small dark class="mr-2">{{
            statusText(SurveyStatus)
          }}</v-chip>
          <span v-if="SurveyStatus != 0"
            >Existing questions are read-only once published; new questions can
            still be added.</span
          >
        </p>
      </div>
      <v-btn
        color="#8950FC"
        elevation="0"
        class="rounded-lg text-white"
        style="height: 44px; font-weight: 600; text-transform: none"
        @click.prevent="openCreateDialog"
      >
        <v-icon left>mdi-plus-circle-outline</v-icon>
        Add Question
      </v-btn>
    </div>

    <div class="d-flex justify-content-center mb-6" v-if="LoadingFlag">
      <b-spinner variant="primary" type="grow" label="Loading..."></b-spinner>
    </div>

    <div class="text-center py-10" v-if="!LoadingFlag && rows.length == 0">
      <h4 class="text-muted font-weight-bold">No questions added yet.</h4>
    </div>

    <v-card
      class="result-card elevation-4 rounded-xl border-0"
      v-if="rows.length > 0"
    >
      <v-card-text class="pa-2 px-md-4">
        <v-data-table
          :headers="tableColumns1"
          :items="rows"
          :items-per-page="-1"
          :loading="TableLoadingFlag"
        >
          <template v-slot:[`item.QuestionText`]="{ item }">
            <div style="max-width: 420px; white-space: normal">
              {{ item.QuestionText }}
            </div>
            <div
              class="text-muted"
              style="font-size: 12px"
              v-if="item.QuestionOptionsTxt"
            >
              Options: {{ item.QuestionOptionsTxt }}
            </div>
          </template>

          <template v-slot:[`item.Actions`]="{ item }">
            <div class="d-flex align-center">
              <v-tooltip bottom v-if="SurveyStatus == 0">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    class="mr-1"
                    @click="openEditDialog(item)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon size="20" color="#EDBE38"
                      >mdi-file-document-edit</v-icon
                    >
                  </v-btn>
                </template>
                <span>Edit</span>
              </v-tooltip>
              <v-tooltip bottom v-if="SurveyStatus == 0">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    @click="confirmDelete(item)"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon size="20" color="#EA5455">mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span>Delete</span>
              </v-tooltip>
              <span
                v-if="SurveyStatus != 0"
                class="text-muted"
                style="font-size: 12px"
                >Locked</span
              >
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Add / Edit Question Dialog -->
    <v-dialog v-model="dialogFlag" persistent max-width="800px">
      <v-card class="rounded-xl">
        <v-card-title class="px-6 py-4" style="background-color: #3a67b1">
          <span class="text-h6 font-weight-bold white--text">{{
            EditId ? "Edit Question" : "Add Question"
          }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogFlag = false"
            ><v-icon color="#FFFFFF">mdi-close</v-icon></v-btn
          >
        </v-card-title>
        <v-card-text class="pa-6">
          <v-form ref="form1" v-model="valid1" lazy-validation>
            <v-row>
              <v-col cols="12" sm="8">
                <label class="form-label"
                  >Question Text <span class="text-danger">*</span></label
                >
                <v-textarea
                  v-model="QuestionText"
                  :rules="QuestionTextRules"
                  outlined
                  dense
                  rows="2"
                ></v-textarea>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="form-label"
                  >Answer Type <span class="text-danger">*</span></label
                >
                <v-select
                  v-model="QuestionType"
                  :items="QuestionTypeOptions"
                  :rules="QuestionTypeRules"
                  item-text="text"
                  item-value="value"
                  outlined
                  dense
                ></v-select>
              </v-col>

              <v-col cols="12" sm="4">
                <label class="form-label">Order</label>
                <v-text-field
                  v-model="OrderColumn"
                  type="number"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4" class="d-flex align-center">
                <v-checkbox v-model="Required" label="Required"></v-checkbox>
              </v-col>

              <v-col
                cols="12"
                sm="4"
                v-if="QuestionType == 5 || QuestionType == 12"
              >
                <label class="form-label">Max File Size (MB)</label>
                <v-text-field
                  v-model="MaxSize"
                  type="number"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col
                cols="12"
                sm="4"
                v-if="QuestionType == 1 || QuestionType == 2"
              >
                <label class="form-label">Max Characters</label>
                <v-text-field
                  v-model="MaxChar"
                  type="number"
                  outlined
                  dense
                ></v-text-field>
              </v-col>

              <v-col
                cols="12"
                v-if="
                  QuestionType == 3 || QuestionType == 7 || QuestionType == 8
                "
              >
                <label class="form-label">
                  Options <span class="text-danger">*</span>
                  <small class="text-muted">(comma-separated)</small>
                </label>
                <v-textarea
                  v-model="QuestionOptions"
                  :rules="QuestionOptionsRules"
                  placeholder="Option 1, Option 2, Option 3..."
                  outlined
                  dense
                  rows="2"
                ></v-textarea>
              </v-col>

              <v-col cols="12" sm="4" v-if="QuestionType == 11">
                <label class="form-label"
                  >Number of Stars <span class="text-danger">*</span></label
                >
                <v-text-field
                  v-model="RatingCount"
                  :rules="RatingCountRules"
                  type="number"
                  min="2"
                  max="10"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="pa-6 pt-0 d-flex justify-end">
          <v-btn
            :disabled="!valid1"
            :loading="SubmitFlag"
            color="#56BDA3"
            class="rounded-lg font-weight-bold px-8 white--text"
            @click.prevent="submitForm"
          >
            Save
          </v-btn>
          <v-btn outlined class="rounded-lg mr-3" @click="dialogFlag = false"
            >Cancel</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";

export default {
  mixins: [common],
  data() {
    return {
      LoadingFlag: true,
      TableLoadingFlag: false,
      SubmitFlag: false,

      SurveyId: this.$route.params.id,
      SurveyTitle: "",
      SurveyStatus: 0,

      rows: [],
      tableColumns1: [
        { text: "#", value: "Order", width: 70 },
        { text: "Question", value: "QuestionText" },
        { text: "Type", value: "QuestionTypeTxt" },
        { text: "Required", value: "RequiredTxt" },
        { text: "Actions", value: "Actions", sortable: false },
      ],

      dialogFlag: false,
      valid1: true,
      EditId: null,

      QuestionText: "",
      QuestionTextRules: [(v) => !!v || "Question text is required"],

      QuestionType: "",
      QuestionTypeRules: [(v) => !!v || "Answer type is required"],
      QuestionTypeOptions: [
        { value: 1, text: "Text" },
        { value: 2, text: "Textarea" },
        { value: 3, text: "Dropdown" },
        { value: 4, text: "Date" },
        { value: 5, text: "File" },
        { value: 6, text: "Time" },
        { value: 7, text: "Radio" },
        { value: 8, text: "Checkbox" },
        { value: 9, text: "Url" },
        { value: 10, text: "Number" },
        { value: 11, text: "Rating" },
        { value: 12, text: "Image" },
      ],

      OrderColumn: 1,
      Required: true,
      MaxSize: "",
      MaxChar: "",
      QuestionOptions: "",
      QuestionOptionsRules: [(v) => !!v || "At least one option is required"],

      RatingCount: 5,
      RatingCountRules: [
        (v) => !!v || "Number of stars is required",
        (v) =>
          (v >= 2 && v <= 10) || "Enter a value between 2 and 10",
      ],
    };
  },
  methods: {
    statusText(status) {
      return status == 1 ? "Published" : status == 2 ? "Closed" : "Draft";
    },
    statusColor(status) {
      return status == 1 ? "#56BDA3" : status == 2 ? "#414042" : "#EDBE38";
    },
    getTableRecords() {
      this.LoadingFlag = true;
      this.TableLoadingFlag = true;
      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/survey-question/lists";

      const thisIns = this;

      this.$http({
        url: add_url,
        method: "POST",
        data: { SurveyId: this.SurveyId },
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          thisIns.LoadingFlag = false;
          thisIns.TableLoadingFlag = false;
          var flag = response.data.flag;
          var records = response.data.records;
          if (flag == 1 || records) {
            thisIns.rows = records.TableData || [];
            thisIns.SurveyTitle = records.SurveyTitle;
            thisIns.SurveyStatus = records.SurveyStatus;
          }
        })
        .catch(function () {
          thisIns.LoadingFlag = false;
          thisIns.TableLoadingFlag = false;
        });
    },
    openCreateDialog() {
      this.EditId = null;
      this.QuestionText = "";
      this.QuestionType = "";
      this.OrderColumn = this.rows.length + 1;
      this.Required = true;
      this.MaxSize = "";
      this.MaxChar = "";
      this.QuestionOptions = "";
      this.RatingCount = 5;
      this.dialogFlag = true;
    },
    openEditDialog(item) {
      this.EditId = item.QuestionId;
      this.QuestionText = item.QuestionText;
      this.QuestionType = item.QuestionType;
      this.OrderColumn = item.Order;
      this.Required = !!item.Required;
      this.MaxSize = item.MaxSize;
      this.MaxChar = item.MaxChar;
      this.QuestionOptions = item.QuestionOptionsTxt;
      this.RatingCount =
        item.QuestionType == 11 && item.QuestionOptionsTxt
          ? item.QuestionOptionsTxt.split(",").length
          : 5;
      this.dialogFlag = true;
    },
    submitForm() {
      var validate1 = this.$refs.form1.validate();
      if (!validate1) {
        this.toast("error", "Kindly fill the required fields");
        return;
      }

      this.SubmitFlag = true;

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url =
        server_url +
        (this.EditId
          ? "api/survey-question/update"
          : "api/survey-question/store");

      var questionOptions = this.QuestionOptions;
      if (this.QuestionType == 11) {
        var starCount = parseInt(this.RatingCount, 10) || 5;
        var stars = [];
        for (var i = 1; i <= starCount; i++) {
          stars.push(String(i));
        }
        questionOptions = stars.join(",");
      }

      var upload = {
        SurveyId: this.SurveyId,
        QuestionId: this.EditId,
        QuestionText: this.QuestionText,
        QuestionType: this.QuestionType,
        Required: this.Required ? 1 : 0,
        OrderColumn: this.OrderColumn,
        MaxSize: this.MaxSize,
        MaxChar: this.MaxChar,
        QuestionOptions: questionOptions,
      };

      const thisIns = this;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          thisIns.SubmitFlag = false;
          var output = response.data.output;
          var flag = response.data.flag;
          if (flag == 1) {
            thisIns.sweetAlert("success", output, false);
            thisIns.dialogFlag = false;
            thisIns.getTableRecords();
          } else {
            thisIns.sweetAlert("error", output, false);
          }
        })
        .catch(function () {
          thisIns.SubmitFlag = false;
        });
    },
    confirmDelete(item) {
      const thisIns = this;
      Swal.fire({
        title: "Delete this question?",
        text: "This cannot be undone.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it",
        confirmButtonColor: "#EA5455",
        cancelButtonColor: "#414042",
      }).then((result) => {
        if (result.isConfirmed) {
          thisIns.deleteQuestion(item);
        }
      });
    },
    deleteQuestion(item) {
      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/survey-question/destroy";

      const thisIns = this;

      this.$http({
        url: add_url,
        method: "POST",
        data: { QuestionId: item.QuestionId },
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          var output = response.data.output;
          var flag = response.data.flag;
          if (flag == 1) {
            thisIns.sweetAlert("success", output, false);
            thisIns.getTableRecords();
          } else {
            thisIns.sweetAlert("error", output, false);
          }
        })
        .catch(function () {});
    },
  },
  beforeMount() {
    this.getTableRecords();
  },
};
</script>

<style lang="scss" scoped>
#survey-questions-edit {
  .banner-header {
    background: linear-gradient(135deg, #3a67b1 0%, #0097d7 100%);
    border-radius: 20px;
    padding: 1.5rem 2.5rem;
    margin-bottom: 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
  }
  .page-title {
    color: #ffffff;
    font-size: 1.8rem;
    font-weight: 700;
    margin: 0;
  }
  .banner-subtitle {
    color: rgba(255, 255, 255, 0.9);
    font-size: 14px;
  }
  .result-card {
    border-radius: 20px !important;
    box-shadow: 0 10px 40px rgba(10, 15, 41, 0.05) !important;
  }
  .form-label {
    font-size: 13px;
    font-weight: 700;
    color: #414042;
    text-transform: uppercase;
    display: block;
    margin-bottom: 6px;
  }
}
</style>
